import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { throttle } from 'lodash';

import VideoPlayer from '../../../../components/CreateMeetingFooter/TrimVideo/VideoPlayer';
import Timeline from '../../../../components/CreateMeetingFooter/TrimVideo/Timeline';
import FilledButton from '../../../../components/Buttons/FilledButton';
import Controls from '../../../../components/CreateMeetingFooter/TrimVideo/Controls';
import TrimHandle from '../../../../components/CreateMeetingFooter/TrimVideo/TrimHandle';

import classes from '../../../../components/CreateMeetingFooter/TrimVideo/styles.module.scss';
import useMutationObserver from '../../../../hooks/useMutationObserver';
import useVideoFrames from '../../../../hooks/useVideoFrames';
import useOnClickOutside from '../../../../hooks/useOnClickOutside';
import { setPendingTrim, clearPendingTrim } from '../../store/slices/editorUISlice';

const PIXELS_PER_SECOND = 50; // Fixed scale for trim handles
const MIN_TRIM_DURATION = 3; // Minimum trim duration in seconds

// Helper to get CSS zoom value
const getZoom = () => {
  const zoomValue = getComputedStyle(document.documentElement)
    .getPropertyValue('--zoom')
    .trim();
  return parseFloat(zoomValue) || 1;
};

/**
 * Client-side TrimVideo component that works with blob URLs
 * Uses Redux for all trim state (including pending/unsaved)
 */
export default function ClientSideTrimVideo({ asset, itemId, itemDuration, existingTrimStart = 0, existingTrimEnd = 0, onSave }) {
  const dispatch = useDispatch();
  const pendingTrimData = useSelector((state) => state.editorUI.pendingTrimData);
  
  // Adaptive initial zoom based on video duration and existing trim
  // When there's existing trim, zoom to show the trimmed area
  const getInitialZoom = (duration, trimStart = 0, trimEnd = 0) => {
    // If there's existing trim, zoom in to show the trimmed area better
    if (trimStart > 0 || trimEnd > 0) {
      return 100; // Full zoom to show the trimmed area clearly
    }

    // No existing trim - use adaptive zoom based on duration
    if (!duration || duration <= 60) return 100; // Short videos: full zoom
    if (duration <= 300) return 50; // Medium videos: 50% zoom
    if (duration <= 600) return 30; // Long videos (5-10 min): 30% zoom
    return 20; // Very long videos (> 10 min): 20% zoom
  };
  
  const [zoom, setZoom] = useState(() => getInitialZoom(itemDuration || asset?.duration || 0, existingTrimStart, existingTrimEnd));
  const [scrollWidth, setScrollWidth] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0); // Track scroll position for handle positioning
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0); // Track current playback time for playhead
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  const [isDraggingTrimHandle, setIsDraggingTrimHandle] = useState(false);
  const [playheadDragTime, setPlayheadDragTime] = useState(null); // Local time during drag
  // Local trim state for immediate updates during dragging (not throttled)
  const [localTrimStart, setLocalTrimStart] = useState(null);
  const [localTrimEnd, setLocalTrimEnd] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // { x, y } | null
  const contextMenuRef = useRef(null);
  const playheadRef = useRef(null);

  const framesListRef = useRef();
  const videoRef = useRef();
  const timelineRef = useRef();
  const [framesListOffset, setFramesListOffset] = useState(0);

  // Initialize pending trim state when modal opens
  useEffect(() => {
    if (itemId && itemDuration) {
      const initialTrimStart = existingTrimStart || 0;
      const initialTrimEnd = existingTrimEnd || 0;
      dispatch(setPendingTrim({
        itemId,
        trimStart: initialTrimStart,
        trimEnd: initialTrimEnd,
      }));
      // Initialize local state
      setLocalTrimStart(initialTrimStart);
      setLocalTrimEnd(initialTrimEnd);

      // Set adaptive initial zoom based on video duration and existing trim
      const initialZoom = getInitialZoom(itemDuration, existingTrimStart, existingTrimEnd);
      setZoom(initialZoom);

      // If there's existing trim, scroll to center on the trimmed area
      if ((initialTrimStart > 0 || initialTrimEnd > 0) && framesListRef.current) {
        // Calculate the center of the trimmed area
        const trimmedStart = initialTrimStart;
        const trimmedEnd = itemDuration - initialTrimEnd;
        const trimmedCenter = (trimmedStart + trimmedEnd) / 2;

        // Scroll to center the trimmed area in the timeline
        // Use requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(() => {
          if (framesListRef.current) {
            const timelineWidth = framesListRef.current.scrollWidth;
            const scrollPosition = (trimmedCenter / itemDuration) * timelineWidth - (framesListRef.current.parentElement?.clientWidth || 0) / 2;
            framesListRef.current.scrollLeft = Math.max(0, scrollPosition);
            setScrollLeft(Math.max(0, scrollPosition));
          }
        });
      }
    }

    // Clear pending trim when component unmounts
    return () => {
      dispatch(clearPendingTrim());
      setLocalTrimStart(null);
      setLocalTrimEnd(null);
    };
  }, [itemId, itemDuration, existingTrimStart, existingTrimEnd, dispatch]);

  // Scroll to trimmed area when modal opens with existing trim
  useEffect(() => {
    if (itemId && itemDuration && (existingTrimStart > 0 || existingTrimEnd > 0) && framesListRef.current && scrollWidth > 0) {
      const trimmedStart = existingTrimStart || 0;
      const trimmedEnd = itemDuration - (existingTrimEnd || 0);
      const trimmedCenter = (trimmedStart + trimmedEnd) / 2;

      // Calculate scroll position to center the trimmed area
      const containerWidth = framesListRef.current.parentElement?.clientWidth || 991; // fallback to approximate width
      const scrollPosition = (trimmedCenter / itemDuration) * scrollWidth - containerWidth / 2;

      framesListRef.current.scrollLeft = Math.max(0, Math.min(scrollPosition, scrollWidth - containerWidth));
      setScrollLeft(Math.max(0, Math.min(scrollPosition, scrollWidth - containerWidth)));
    }
  }, [itemId, itemDuration, existingTrimStart, existingTrimEnd, scrollWidth]);

  useMutationObserver(framesListRef, () => {
    const width = framesListRef.current?.scrollWidth || 0;
    setScrollWidth(width);
  });

  // Use blob URL for frame generation
  const {
    frames,
    frameCount,
    size,
    isGenerating: isGeneratingFrames,
    generationError: frameGenerationError,
  } = useVideoFrames(asset?.url);
  const frameWidth =
    size?.width && size?.height
      ? Math.round(size.width / (size.height / 65))
      : 115;

  useEffect(() => {
    // Recalculate scrollWidth when zoom changes (affects number of frames shown)
    // Use requestAnimationFrame to ensure DOM has updated after zoom change
    const updateScrollWidth = () => {
      if (framesListRef.current) {
        const width = framesListRef.current.scrollWidth || 0;
    setScrollWidth(width);
        
        // Measure actual offset of framesList from timelineWrapper
        // Account for CSS zoom
        if (timelineRef.current) {
          const cssZoom = getZoom();
          const framesRect = framesListRef.current.getBoundingClientRect();
          const timelineRect = timelineRef.current.getBoundingClientRect();
          const offset = (framesRect.left - timelineRect.left) / cssZoom;
          setFramesListOffset(offset);
        }
      }
    };
    
    // Small delay to ensure DOM has updated after zoom change
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(updateScrollWidth);
    }, 0);
    
    return () => clearTimeout(timeoutId);
  }, [zoom, frames.length, frameWidth]);

  // Get trim values - use local state if dragging, otherwise Redux or existing
  const duration = itemDuration || asset?.duration || 0;
  
  // Determine current trim start value
  let currentTrimStart;
  if (localTrimStart !== null) {
    currentTrimStart = localTrimStart;
  } else if (pendingTrimData?.itemId === itemId) {
    currentTrimStart = pendingTrimData.trimStart;
  } else {
    currentTrimStart = existingTrimStart || 0;
  }
  
  // Determine current trim end value
  let currentTrimEnd;
  if (localTrimEnd !== null) {
    currentTrimEnd = localTrimEnd;
  } else if (pendingTrimData?.itemId === itemId) {
    currentTrimEnd = pendingTrimData.trimEnd;
  } else {
    currentTrimEnd = existingTrimEnd || 0;
  }
  
  const startTime = currentTrimStart;
  const endTime = duration - currentTrimEnd;
  const trimmedDuration = endTime - startTime;

  const isSaveEnabled =
    (startTime !== 0 || currentTrimEnd > 0.5) && trimmedDuration >= MIN_TRIM_DURATION;

  // Update currentPlaybackTime when trim boundaries change to keep playhead within bounds
  useEffect(() => {
    if (duration > 0 && startTime !== undefined && endTime !== undefined && videoRef.current) {
      const currentTime = videoRef.current.currentTime || currentPlaybackTime;
      const clampedTime = Math.max(startTime, Math.min(endTime, currentTime));
      
      // Only update if the current time is outside the new boundaries
      if (currentTime < startTime || currentTime > endTime) {
        setCurrentPlaybackTime(clampedTime);
        videoRef.current.currentTime = clampedTime;
      }
    }
  }, [startTime, endTime, duration]); // Only update when boundaries change

  // Throttled Redux update for trim handles (less aggressive since local state handles UI)
  const throttledUpdateTrim = useMemo(
    () => throttle((trimStart, trimEnd) => {
      dispatch(setPendingTrim({
        itemId,
        trimStart,
        trimEnd,
      }));
    }, 50), // Update max 20 times per second (local state updates immediately)
    [dispatch, itemId]
  );

  // Cleanup throttled function on unmount
  useEffect(() => {
    return () => {
      throttledUpdateTrim.cancel();
    };
  }, [throttledUpdateTrim]);

  // Hide context menu whenever marker drag state changes (start or end)
  useEffect(() => {
    setContextMenu(null);
  }, [isDraggingTrimHandle]);

  // Handle trim start (called by TrimHandle when drag begins)
  const handleTrimStart = () => {
    setIsDraggingTrimHandle(true);
  };

  // Handle trim move (called by TrimHandle)
  // Convert pixel delta to time delta using actual timeline width
  const handleTrimMove = (side, deltaPixels) => {
    // Use scrollWidth (actual timeline width) to convert pixels to time
    const deltaSeconds = scrollWidth > 0 
      ? (deltaPixels / scrollWidth) * duration 
      : (deltaPixels / PIXELS_PER_SECOND); // Fallback to fixed scale if scrollWidth not ready
    
    // Get current values (local state takes precedence)
    const currentStart = localTrimStart !== null ? localTrimStart : currentTrimStart;
    const currentEnd = localTrimEnd !== null ? localTrimEnd : currentTrimEnd;
    
    let newTrimStart = currentStart;
    let newTrimEnd = currentEnd;
    
    if (side === 'left') {
      // Trimming from start
      newTrimStart = Math.max(0, currentStart + deltaSeconds);
      // Ensure minimum trim duration: endTime - startTime >= MIN_TRIM_DURATION
      // endTime = duration - currentEnd, so: duration - currentEnd - newTrimStart >= MIN_TRIM_DURATION
      const maxTrimStart = duration - currentEnd - MIN_TRIM_DURATION;
      newTrimStart = Math.min(newTrimStart, maxTrimStart);
    } else {
      // Trimming from end
      newTrimEnd = Math.max(0, currentEnd + deltaSeconds);
      // Ensure minimum trim duration: endTime - startTime >= MIN_TRIM_DURATION
      // endTime = duration - newTrimEnd, startTime = currentStart
      // So: duration - newTrimEnd - currentStart >= MIN_TRIM_DURATION
      const maxTrimEnd = duration - currentStart - MIN_TRIM_DURATION;
      newTrimEnd = Math.min(newTrimEnd, maxTrimEnd);
    }
    
    // Update local state immediately for responsive UI (not throttled)
    setLocalTrimStart(newTrimStart);
    setLocalTrimEnd(newTrimEnd);
    
    // Throttled Redux update (for persistence)
    throttledUpdateTrim(newTrimStart, newTrimEnd);
  };

  // Flush throttled updates and sync local state to Redux on drag end
  const handleTrimEnd = () => {
    setIsDraggingTrimHandle(false);
    throttledUpdateTrim.flush();
    // Sync final values to Redux
    if (localTrimStart !== null && localTrimEnd !== null) {
      dispatch(setPendingTrim({
        itemId,
        trimStart: localTrimStart,
        trimEnd: localTrimEnd,
      }));
    }
  };
  
  // Handle setting start time from context menu
  const handleSetStartTime = () => {
    const currentTime = playheadDragTime !== null ? playheadDragTime : currentPlaybackTime;
    // Ensure minimum trim duration: endTime - startTime >= MIN_TRIM_DURATION
    // endTime = duration - currentTrimEnd, so: duration - currentTrimEnd - newTrimStart >= MIN_TRIM_DURATION
    const maxTrimStart = duration - currentTrimEnd - MIN_TRIM_DURATION;
    const newTrimStart = Math.max(0, Math.min(currentTime, maxTrimStart));
    
    setLocalTrimStart(newTrimStart);
    throttledUpdateTrim(newTrimStart, currentTrimEnd);
    setContextMenu(null);
  };
  
  // Handle setting end time from context menu
  const handleSetEndTime = () => {
    const currentTime = playheadDragTime !== null ? playheadDragTime : currentPlaybackTime;
    // Ensure minimum trim duration: endTime - startTime >= MIN_TRIM_DURATION
    // endTime = duration - newTrimEnd, startTime = currentTrimStart
    // So: duration - newTrimEnd - currentTrimStart >= MIN_TRIM_DURATION
    const maxTrimEnd = duration - currentTrimStart - MIN_TRIM_DURATION;
    const newTrimEnd = Math.max(0, Math.min(duration - currentTime, maxTrimEnd));
    
    setLocalTrimEnd(newTrimEnd);
    throttledUpdateTrim(currentTrimStart, newTrimEnd);
    setContextMenu(null);
  };
  
  // Close context menu when clicking outside (but not when clicking on playhead)
  useOnClickOutside(contextMenuRef, () => {
    if (contextMenu) {
      setContextMenu(null);
    }
  }, playheadRef);

  // Format time in minutes:seconds for badges
  const formatTimeInMinutes = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Create a section-like object for Timeline component compatibility
  const sectionLike = {
    duration,
    id: asset?.id,
  };

  // Calculate handle positions to align with frames
  // Use measured offset if available, otherwise fall back to calculated offset
  const timelineWidth = scrollWidth || duration * PIXELS_PER_SECOND;
  const framesStartOffset = framesListOffset > 0 ? framesListOffset : (42 + 38); // Use measured offset or fallback
  
  // Position handles to align with frames
  // For time 0: position = framesStartOffset - scrollLeft (aligns with first frame)
  // For time duration: position = framesStartOffset - scrollLeft + scrollWidth (aligns with last frame)
  const leftHandlePosition = framesStartOffset - scrollLeft + (startTime / duration) * timelineWidth;
  const rightHandlePosition = framesStartOffset - scrollLeft + ((duration - currentTrimEnd) / duration) * timelineWidth;
  
  // Calculate playhead position based on current playback time
  // Playhead shows where we are in the video on the timeline
  // Clamp playback time to trim boundaries and position relative to full timeline
  const displayPlaybackTime = playheadDragTime !== null ? playheadDragTime : currentPlaybackTime;
  const playheadTime = Math.max(startTime, Math.min(endTime, displayPlaybackTime));
  // Position playhead to align with timestamps: timestamps start at left: 0 of scroll container
  // So playhead should be positioned relative to framesList, accounting for scroll
  const playheadPosition = framesStartOffset - scrollLeft + (playheadTime / duration) * timelineWidth;
  
  // Convert pixel position to time for playhead dragging and timeline clicking
  // Account for CSS zoom in all coordinate calculations
  const getTimeFromPixelPosition = (pixelX) => {
    if (!timelineRef.current || duration === 0 || timelineWidth === 0) return startTime;
    
    const cssZoom = getZoom();
    const timelineRect = timelineRef.current.getBoundingClientRect();
    // Divide by zoom to get actual coordinates
    const relativeX = (pixelX / cssZoom) - (timelineRect.left / cssZoom) - framesStartOffset + scrollLeft;
    const percentage = Math.max(0, Math.min(relativeX / timelineWidth, 1));
    const time = percentage * duration;
    return Math.max(startTime, Math.min(endTime, time));
  };
  
  // Handle timeline click to seek
  const handleTimelineClick = (e) => {
    if (isGeneratingFrames || frameGenerationError) {
      return;
    }
    // Don't seek if clicking on trim handles or playhead (they have their own handlers)
    // Check if click originated from trim handles or playhead
    const clickedElement = e.target;
    const isTrimHandle = clickedElement.closest(`.${classes.trimHandleWrapper}`) !== null ||
                        clickedElement.closest(`.${classes.trimHandlesContainer}`) !== null;
    const isPlayhead = clickedElement.closest(`.${classes.playhead}`) !== null;
    
    if (isTrimHandle || isPlayhead) {
      return;
    }
    
    const seekTime = getTimeFromPixelPosition(e.clientX);
    if (videoRef.current && !isDraggingTrimHandle) {
      videoRef.current.currentTime = seekTime;
      setCurrentPlaybackTime(seekTime);
    }
  };
  
  // Handle playhead click to show menu
  const handlePlayheadClick = (x, y) => {
    const cssZoom = getZoom();
    setContextMenu({
      x: x / cssZoom,
      y: y / cssZoom,
    });
  };
  
  // Handle playhead drag start
  const handlePlayheadMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    const startY = e.clientY;
    let hasMoved = false;
    
    const wasPlaying = isVideoPlaying;
    if (wasPlaying) {
      videoRef.current?.pause();
    }
    
    if (!timelineRef.current) return;
    
    const handleMouseMove = (moveEvent) => {
      const cssZoom = getZoom();
      // Account for zoom in drag detection threshold
      const deltaX = Math.abs((moveEvent.clientX - startX) / cssZoom);
      const deltaY = Math.abs((moveEvent.clientY - startY) / cssZoom);
      
      // If mouse moved more than 5px (in actual pixels), consider it a drag
      if (deltaX > 5 || deltaY > 5) {
        hasMoved = true;
        
        if (!isDraggingPlayhead) {
          setIsDraggingPlayhead(true);
        }
        
        moveEvent.preventDefault();
        // Calculate time directly from mouse position - playhead will follow because
        // its position is calculated from playheadDragTime
        const time = getTimeFromPixelPosition(moveEvent.clientX);
        setPlayheadDragTime(time);
        setCurrentPlaybackTime(time); // Update timecode during drag
        if (videoRef.current) {
          videoRef.current.currentTime = time;
        }
      }
    };
    
    const handleMouseUp = (mouseUpEvent) => {
      // If mouse didn't move much, it's a click - show menu
      if (!hasMoved) {
        handlePlayheadClick(mouseUpEvent.clientX, mouseUpEvent.clientY);
      } else {
        // If we dragged, get final time directly from mouse position
        const finalTime = getTimeFromPixelPosition(mouseUpEvent.clientX);
        setCurrentPlaybackTime(finalTime);
        setPlayheadDragTime(finalTime); // Set this so playhead position updates before clearing
        if (videoRef.current) {
          videoRef.current.currentTime = finalTime;
        }
      }
      
      setIsDraggingPlayhead(false);
      // Small delay before clearing playheadDragTime to ensure position updates
      setTimeout(() => {
        setPlayheadDragTime(null);
      }, 0);
      
      // Resume playback if it was playing before drag
      if (wasPlaying && videoRef.current && hasMoved) {
        videoRef.current.play().catch(() => {});
      }
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleSave = () => {
    if (onSave) {
      // Flush any pending throttled updates
      throttledUpdateTrim.flush();
      
      // Use current trim values (from pending or existing)
      const finalTrimStart = currentTrimStart;
      const finalTrimEnd = currentTrimEnd;
      
      onSave({
        startTime: finalTrimStart,
        endTime: duration - finalTrimEnd,
        originalDuration: duration,
      });
      
      // Clear pending trim
      dispatch(clearPendingTrim());
    }
  };

  return (
    <div className={classes.TrimVideo}>
      <div className={classes.videoContainer}>
        <VideoPlayer
          videoUrl={asset?.url}
          videoRef={videoRef}
          isVideoPlaying={isVideoPlaying}
          setIsVideoPlaying={setIsVideoPlaying}
          startTime={startTime}
          endTime={endTime}
        />
      </div>
      <Controls
        zoom={zoom}
        setZoom={setZoom}
        frameWidth={frameWidth}
        section={sectionLike}
        videoRef={videoRef}
        isVideoPlaying={isVideoPlaying}
        startTime={startTime}
        endTime={endTime}
        frameCount={frameCount}
        onTimeUpdate={setCurrentPlaybackTime}
        currentTime={currentPlaybackTime}
      />
      <div 
        className={classes.timelineWrapper} 
        ref={timelineRef}
        onClick={handleTimelineClick}
      >
        <div className={classes.timelineContainerWrapper}>
          <Timeline
            section={sectionLike}
            zoom={zoom}
            frameWidth={frameWidth}
            framesListRef={framesListRef}
            scrollWidth={scrollWidth}
            setScrollLeft={setScrollLeft}
            frames={frames}
            frameCount={frameCount}
            isGenerating={isGeneratingFrames}
            generationError={frameGenerationError}
          />
          {!isGeneratingFrames && !frameGenerationError && (
            <>
          {/* Bracket handles overlay - positioned relative to timeline container */}
          <div className={classes.trimHandlesContainer}>
          {/* Left disabled area overlay - covers frames area before left trim handle */}
          {leftHandlePosition > framesStartOffset && (
            <div
              className={classes.disabledOverlay}
              style={{
                left: `${framesStartOffset}px`,
                width: `${leftHandlePosition - framesStartOffset}px`,
              }}
            />
          )}
          {/* Right disabled area overlay - covers frames area after right trim handle */}
          {rightHandlePosition < framesStartOffset + timelineWidth && (
            <div
              className={classes.disabledOverlay}
              style={{
                left: `${rightHandlePosition}px`,
                      width: `${framesStartOffset + timelineWidth - rightHandlePosition}px`,
              }}
            />
          )}
          <div
            className={classes.trimHandleWrapper}
            style={{
              left: `${leftHandlePosition}px`,
            }}
            data-testid="trim-handle-left"
          >
            <TrimHandle
              side="left"
              onTrimStart={handleTrimStart}
              onTrimMove={(delta) => handleTrimMove('left', delta)}
              onTrimEnd={handleTrimEnd}
            />
            {/* Start time badge */}
            <div className={classes.trimTimeBadge}>
              {formatTimeInMinutes(startTime)}
            </div>
          </div>
          <div
            className={classes.trimHandleWrapper}
            style={{
              left: `${rightHandlePosition}px`,
            }}
            data-testid="trim-handle-right"
          >
            <TrimHandle
              side="right"
              onTrimStart={handleTrimStart}
              onTrimMove={(delta) => handleTrimMove('right', delta)}
              onTrimEnd={handleTrimEnd}
            />
            {/* End time badge */}
            <div className={classes.trimTimeBadge}>
              {formatTimeInMinutes(endTime)}
            </div>
          </div>
          {/* Playhead indicator - draggable for seeking */}
          {duration > 0 && (
            <div
              ref={playheadRef}
              className={`${classes.playhead} ${isDraggingPlayhead ? classes.dragging : ''}`}
              style={{
                left: `${playheadPosition}px`,
              }}
              onMouseDown={handlePlayheadMouseDown}
            >
              <div className={classes.playheadLine} />
              <div className={classes.playheadCircle} />
            </div>
          )}
      </div>
      {/* Context menu for playhead - rendered outside trimHandlesContainer to avoid pointer-events issues */}
      {!frameGenerationError && !isGeneratingFrames && contextMenu && (
        <div
          ref={contextMenuRef}
          className={classes.contextMenu}
          style={{
            position: 'fixed',
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
            transform: 'translate(-50%, -100%)',
                    marginTop: '-8px',
            zIndex: 10000,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className={classes.contextMenuItem}
            onClick={handleSetStartTime}
          >
            Set start time
          </button>
          <button
            type="button"
            className={classes.contextMenuItem}
            onClick={handleSetEndTime}
          >
            Set end time
          </button>
        </div>
      )}
            </>
          )}
        </div>
      </div>
      <div className={classes.buttons}>
        <FilledButton
          width={263}
          color="#FDB258"
          isDisabled={!isSaveEnabled}
          onClick={handleSave}
        >
          Save Changes
        </FilledButton>
      </div>
    </div>
  );
}

